import { useTheme } from "@mui/material";
import { ApplicationState } from "@store/index";
import { useAppSelector } from "index";
import { useState, useEffect } from "react";

export type Size = {
  width: number;
  height: number;
};

/**
 * Returns the inner Width of the whole window, without the side
 * @returns Size object
 */
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<Size>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}

/**
 * returns the left and right paddings from the current theme
 * but checked if they are currently active and stuff
 * also is there a fullscreen?
 * everything matters here
 *
 * @returns left and right
 */
export function useSidebarPaddings() {
  const activeFilters = useAppSelector((state: ApplicationState) => state.filters.active);
  const navSidebarOpen = useAppSelector((state: ApplicationState) => state.options.navSidebarOpen);
  const configSidebarOpen = useAppSelector((state: ApplicationState) => state.options.configSidebarOpen);
  const theme = useTheme();
  const fullscreenActive = document.fullscreenElement !== null;
  const padLeft = navSidebarOpen ? theme.sidebar.navigation.width : theme.sidebar.navigation.collapsed;
  const padRight =
    activeFilters.length !== 0 && configSidebarOpen ? theme.sidebar.config.width : theme.sidebar.config.collapsed;
  return {
    left: fullscreenActive ? 0 : padLeft,
    right: fullscreenActive ? 0 : padRight,
  };
}

/**
 * returns the size for content between the two sidebars
 * also returns the correct size when the fullscreen is activated
 *
 * @returns Size object
 */
export function useContentSize() {
  const { left, right } = useSidebarPaddings();
  const windowSize = useWindowSize();
  const theme = useTheme();
  return {
    width: windowSize.width - (left + right),
    height: windowSize.height - theme.sidebar.headerHeight - theme.sidebar.footerHeight - theme.sidebar.bottomBarHeight,
  };
}
